import { noValue } from "./general";

/** A condition an item must satisfy to be included in a query result. */
export type Predicate<T> = (item: T) => boolean;

/**
 * Builds and runs simple in-memory queries over an array.
 *
 * Conditions added with `where` are combined with AND semantics. `skip`
 * discards matching items before `take` limits the returned results; neither
 * affects `count`.
 */
export class QueryBuilder<T> {
	private predicates: Predicate<T>[] = [];
	private offset: number | undefined;
	private limit: number | undefined;

	constructor(private data?: T[]) {}

	/** Adds a condition to the query. Conditions are evaluated in insertion order. */
	where(predicate: Predicate<T>): this {
		this.predicates.push(predicate);
		return this;
	}

	/** Sets the number of matching items to skip before collecting results. */
	skip(count: number | undefined): this {
		this.offset = count;
		return this;
	}

	/** Sets the maximum number of matching items returned by `execute`. */
	take(count: number | undefined): this {
		this.limit = count;
		return this;
	}

	/** Counts every item matching the query, regardless of the configured limit. */
	count(data?: T[]): number {
		data = data ?? this.data;
		if (!data) return 0;
		if (noValue(this.predicates)) return data.length;

		let count = 0;
		for (const item of data) {
			if (this.matches(item)) count++;
		}
		return count;
	}

	/** Returns the matching items, preserving their input order. */
	execute(data?: T[]): T[] {
		data = data ?? this.data;
		if (!data) return [];
		if (noValue(this.predicates) && noValue(this.offset) && noValue(this.limit)) return data;

		const result: T[] = [];
		let remainingToSkip = this.offset ?? 0;
		let remaining = this.limit ?? Infinity;
		for (const item of data) {
			if (remaining < 1) break;
			if (!this.matches(item)) continue;
			if (remainingToSkip > 0) {
				remainingToSkip--;
				continue;
			}
			result.push(item);
			remaining--;
		}
		return result;
	}

	private matches(item: T): boolean {
		for (const predicate of this.predicates) {
			if (!predicate(item)) return false;
		}
		return true;
	}
}
